import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Header from '../components/Header';
import './HomeScreen.css';
import { logEvent } from '../analytics';

import meditaZenthor from '../images/medita_zenthor.jpg';
import respiraZenthor from '../images/respira_zenthor.jpg';

const complementaryCards = [
  {
    category: 'Fortalece tu paz mental',
    title: 'Prácticas de Mindfulness',
    sessions: '2 meditaciones disponibles',
    bg: `url("${meditaZenthor}")`,
    to: '/meditacion',
    aspectRatio: '16/17'
  },
  {
    category: 'Domina tus reacciones',
    title: 'Herramientas para momentos intensos',
    sessions: '2 herramientas disponibles',
    bg: `url("${respiraZenthor}")`,
    to: '/enojo',
    aspectRatio: '16/24'
  },
  /*   {
      category: 'Comunica con Firmeza',
      title: 'Nivel 1 - Aprendé a decir NO sin culpa',
      sessions: '2 niveles completados',
      bg: 'url("https://lh3.googleusercontent.com/aida-public/AB6AXuBFMfobkoCWrt1ovwd40zriSsvsMpCgWM2LxEghPVS5NKK7WDM3-Xh8lD7lC5bGVGTGJGxFDkgaiGNgCGiwPNTXdOCXhWJgiBf_OXrqBTFmf9eLn_bEiL16F6VXjBueRGkWM-twZdzG3ZKOre-V7xzcUdF_DEq_HOj3Lr8ofCWU8zXGU3MyzX94G3okYidXXMBunBoBVpkhbndPaElgq4LJx_AZi2Dm9R4A-b2Rg4L8FCAaiaF_f2_YjoUnoE7-j9vys-tUEbIPOHU")',
      to: '/respiracion',
    }, */
];

const suggestions = [
  "¿Cómo puedo dejar de pensar tanto?",
  "No me siento suficiente conmigo",
  "Estoy muy ansioso por el futuro",
  "Quiero ser más disciplinado",
  "Quiero mejorar con mi familia",
  "Hoy no logro concentrarme bien",
  "Me enojo muy rápido con mi pareja",
  "Siento que mi familia no me entiende",
  "Quiero ser buen padre, pero me da miedo",
  "Siento que nadie cuida de mí",
  "No quiero gritar, pero me cuesta",
  "Exploto por cosas pequeñas",
  "No sé hablar con mi jefe sin miedo",
  "Quiero espacio sin herir a mi pareja",
  "Siempre cedo, no sé poner límites",
  "¿Cómo pido perdón sin sentirme débil?"
];

function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

const HomeScreen = ({ userId, onLogout }) => {
  const userName = localStorage.getItem('user_name');
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const shuffledSuggestions = useMemo(() => shuffle(suggestions), []);
  // Duplicar sugerencias para bucle infinito
  const carouselSuggestions = [...shuffledSuggestions, ...shuffledSuggestions];

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const createInitialMessage = async (messageContent) => {
    try {
      const newSessionId = `session-${userId}-${Date.now()}`;

      // Crear el documento de la sesión
      const sessionDocRef = doc(db, 'session-messages', newSessionId);
      await setDoc(sessionDocRef, {
        user_id: userId,
        created_at: serverTimestamp(),
        current_status: null
      });

      // Crear el primer mensaje del usuario
      const messagesRef = collection(db, `session-messages/${newSessionId}/messages`);
      await addDoc(messagesRef, {
        content: messageContent,
        role: 'user',
        message_type: 'text',
        timestamp: serverTimestamp(),
        created_at: serverTimestamp(),
        user_id: userId
      });

      return newSessionId;
    } catch (error) {
      console.error('Error creating initial message:', error);
      throw error;
    }
  };

  const handleInputSend = async (e) => {
    e.preventDefault();
    if (!inputValue.trim() || isLoading) return;

    // Track custom message
    logEvent('Chat', 'Custom Message', inputValue.trim().substring(0, 50));

    setIsLoading(true);
    try {
      const sessionId = await createInitialMessage(inputValue.trim());
      navigate(`/chat/${sessionId}`);
    } catch (error) {
      console.error('Error creating session:', error);
      // Fallback: navegar sin crear mensaje
      const newSessionId = `session-${userId}-${Date.now()}`;
      navigate(`/chat/${newSessionId}?q=${encodeURIComponent(inputValue.trim())}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    if (isLoading) return;

    // Track suggestion click
    logEvent('Suggestion', 'Click', suggestion);

    setIsLoading(true);
    try {
      const sessionId = await createInitialMessage(suggestion);
      navigate(`/chat/${sessionId}`);
    } catch (error) {
      console.error('Error creating session:', error);
      // Fallback: navegar sin crear mensaje
      const newSessionId = `session-${userId}-${Date.now()}`;
      navigate(`/chat/${newSessionId}?q=${encodeURIComponent(suggestion)}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="home-screen">
      {/* Overlay de loading */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner"></div>
            <p>Creando conversación...</p>
          </div>
        </div>
      )}
      <div className="header-section-main-page">
        <h1 className="welcome-text-header">Zenthor</h1>
        <button onClick={onLogout} className="logout-button">
          Cerrar Sesión
        </button>
      </div>
      {/* Header
      <div className="header-section">
        <div className="header-title">Zenthor</div>
        <button className="header-settings">
          <svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" fill="currentColor" viewBox="0 0 256 256">
            <path d="M128,80a48,48,0,1,0,48,48A48.05,48.05,0,0,0,128,80Zm0,80a32,32,0,1,1,32-32A32,32,0,0,1,128,160Zm88-29.84q.06-2.16,0-4.32l14.92-18.64a8,8,0,0,0,1.48-7.06,107.21,107.21,0,0,0-10.88-26.25,8,8,0,0,0-6-3.93l-23.72-2.64q-1.48-1.56-3-3L186,40.54a8,8,0,0,0-3.94-6,107.71,107.71,0,0,0-26.25-10.87,8,8,0,0,0-7.06,1.49L130.16,40Q128,40,125.84,40L107.2,25.11a8,8,0,0,0-7.06-1.48A107.6,107.6,0,0,0,73.89,34.51a8,8,0,0,0-3.93,6L67.32,64.27q-1.56,1.49-3,3L40.54,70a8,8,0,0,0-6,3.94,107.71,107.71,0,0,0-10.87,26.25,8,8,0,0,0,1.49,7.06L40,125.84Q40,128,40,130.16L25.11,148.8a8,8,0,0,0-1.48,7.06,107.21,107.21,0,0,0,10.88,26.25,8,8,0,0,0,6,3.93l23.72,2.64q1.49,1.56,3,3L70,215.46a8,8,0,0,0,3.94,6,107.71,107.71,0,0,0,26.25,10.87,8,8,0,0,0,7.06-1.49L125.84,216q2.16.06,4.32,0l18.64,14.92a8,8,0,0,0,7.06,1.48,107.21,107.21,0,0,0,26.25-10.88,8,8,0,0,0,3.93-6l2.64-23.72q1.56-1.48,3-3L215.46,186a8,8,0,0,0,6-3.94,107.71,107.71,0,0,0,10.87-26.25,8,8,0,0,0-1.49-7.06Zm-16.1-6.5a73.93,73.93,0,0,1,0,8.68,8,8,0,0,0,1.74,5.48l14.19,17.73a91.57,91.57,0,0,1-6.23,15L187,173.11a8,8,0,0,0-5.1,2.64,74.11,74.11,0,0,1-6.14,6.14,8,8,0,0,0-2.64,5.1l-2.51,22.58a91.32,91.32,0,0,1-15,6.23l-17.74-14.19a8,8,0,0,0-5-1.75h-.48a73.93,73.93,0,0,1-8.68,0,8,8,0,0,0-5.48,1.74L100.45,215.8a91.57,91.57,0,0,1-15-6.23L82.89,187a8,8,0,0,0-2.64-5.1,74.11,74.11,0,0,1-6.14-6.14,8,8,0,0,0-5.1-2.64L46.43,170.6a91.32,91.32,0,0,1-6.23-15l14.19-17.74a8,8,0,0,0,1.74-5.48,73.93,73.93,0,0,1,0-8.68,8,8,0,0,0-1.74-5.48L40.2,100.45a91.57,91.57,0,0,1,6.23-15L69,82.89a8,8,0,0,0,5.1-2.64,74.11,74.11,0,0,1,6.14-6.14A8,8,0,0,0,82.89,69L85.4,46.43a91.32,91.32,0,0,1,15-6.23l17.74,14.19a8,8,0,0,0,5.48,1.74,73.93,73.93,0,0,1,8.68,0,8,8,0,0,0,5.48-1.74L155.55,40.2a91.57,91.57,0,0,1,15,6.23L173.11,69a8,8,0,0,0,2.64,5.1,74.11,74.11,0,0,1,6.14,6.14,8,8,0,0,0,5.1,2.64l22.58,2.51a91.32,91.32,0,0,1,6.23,15l-14.19,17.74A8,8,0,0,0,199.87,123.66Z" />
          </svg>
        </button>
      </div> */}
      <div className='home-container'>
        {/* Bienvenida */}
        <div className="welcome-section">
          <h3 className="welcome-text">Hola, {userName === 'null' ? 'Invitado' : userName} <span role="img" aria-label="wave">👋🏻</span> ¿Cómo te sentís hoy?</h3>
        </div>

        {/* Input */}
        <form className="ask-input" onSubmit={handleInputSend}>
          <textarea
            placeholder={isLoading ? "Creando conversación..." : "Escribe como te sientes..."}
            value={inputValue}
            onChange={handleInputChange}
            className="ask-textarea"
            rows={2}
            disabled={isLoading}
            ref={el => {
              if (el) {
                el.style.height = 'auto';
                el.style.height = Math.min(el.scrollHeight, 120) + 'px';
              }
            }}
            style={{ maxHeight: 120, minHeight: 48, resize: 'none', overflowY: 'auto' }}
          />
          <div className="ask-bar">
            <button
              type="button"
              className="ask-mic-btn"
              aria-label="Grabar audio"
              disabled={isLoading}
            >
              {/* Micrófono (SVG Material Design filled) */}
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path d="M12 15c1.66 0 3-1.34 3-3V7c0-1.66-1.34-3-3-3s-3 1.34-3 3v5c0 1.66 1.34 3 3 3zm5-3c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.07 2.13 5.64 5 6.32V21h2v-2.68c2.87-.68 5-3.25 5-6.32h-2z" fill="currentColor" />
              </svg>
            </button>
            <button
              type="submit"
              className="ask-send-btn"
              aria-label="Enviar"
              disabled={!inputValue.trim() || isLoading}
            >
              {isLoading ? (
                /* Spinner de loading */
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray="31.416" strokeDashoffset="31.416" style={{ animation: 'spin 1s linear infinite' }}>
                    <animateTransform
                      attributeName="transform"
                      type="rotate"
                      values="0 12 12;360 12 12"
                      dur="1s"
                      repeatCount="indefinite"
                    />
                  </circle>
                </svg>
              ) : (
                /* Flecha de enviar (SVG) */
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 12L21 3L12 21L11 13L3 12Z" fill="currentColor" />
                </svg>
              )}
            </button>
          </div>
        </form>

        {/* Sugerencias */}
        <div className="suggestions-list">
          <div className="suggestions-carousel-track css-carousel">
            {carouselSuggestions.map((s, idx) => (
              <button
                key={idx}
                type="button"
                className="suggestion-btn"
                onClick={() => handleSuggestionClick(s)}
                disabled={isLoading}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Complementary */}
        <div className="complementary-section">
          <div className="complementary-title">Otras herramientas</div>
          {complementaryCards.map((card, idx) => (
            <Link
              to={card.to}
              className="complementary-card"
              key={idx}
              style={{ textDecoration: 'none' }}
              onClick={() => logEvent('Tool', 'Click', card.title)}
            >
              <div className="complementary-card-text">
                <p className="complementary-card-category text-sm">{card.category}</p>
                <p className="complementary-card-title text-middle">{card.title}</p>
                <p className="complementary-card-sessions text-sm">{card.sessions}</p>
              </div>
              <div className="complementary-card-img-2" style={{ backgroundImage: card.bg, aspectRatio: card.aspectRatio }}></div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
};

export default HomeScreen; 